import React from "react";
import { Button, Slider, Input, Spin, Tooltip } from "antd";
import {
  SelectOutlined,
  EditOutlined,
  FontSizeOutlined,
  PlusOutlined,
  LineOutlined,
  BorderOutlined,
  Loading3QuartersOutlined,
  ArrowRightOutlined,
  CaretUpOutlined,
  CloudOutlined,
  DeleteOutlined,
  LinkOutlined,
  ScanOutlined,
  SearchOutlined,
  UpOutlined,
  DownOutlined,
  DragOutlined,
  MessageOutlined,
} from "@ant-design/icons";
import { TOOLS, SHAPES, COLORS } from "../constants";
import AskAIButton from "../../help/AskAIButton";

const DrawingToolbar = ({
  currentTool,
  currentShape,
  color,
  strokeWidth,
  textBold,
  textItalic,
  textUnderline,
  textStrike,
  fontSize,
  showTextInput = false,
  selectedAnnotationIndex,
  canDeleteSelectedAnnotation,
  zoom,
  visualZoom,
  showSearch,
  searchQuery,
  searchResults,
  currentMatchIndex,
  isSearching,
  searchInputRef,
  onToolClick,
  onShapeClick,
  onColorChange,
  onStrokeWidthChange,
  onTextStyleChange,
  onFontSizeChange,
  onDeleteAnnotation,
  onZoomChange,
  onSearchToggle,
  onSearchChange,
  onSearchNavigate,
  detectingSheetLinks = false,
  canDetectSheetLinks = true,
  onDetectSheetLinks,
}) => {
  const showTextControls =
    currentTool === TOOLS.TEXT || currentTool === TOOLS.CALLOUT || showTextInput;
  // Prevent textarea blur when interacting with toolbar while editing text.
  const keepTextFocus = (e) => {
    if (showTextInput) e.preventDefault();
  };

  return (
    <div
      data-drawing-toolbar
      style={{
        display: "flex",
        marginBottom: 8,
        gap: 8,
        flexWrap: "wrap",
        alignItems: "center",
        position: "sticky",
        top: 0,
        zIndex: 10,
        background: "white",
        padding: 8,
        borderBottom: "1px solid #eee",
      }}
    >
      <Button
        icon={<SelectOutlined />}
        type={currentTool === TOOLS.SELECT ? "primary" : "default"}
        onClick={() => onToolClick(TOOLS.SELECT)}
      />
      <Button
        icon={<DragOutlined />}
        type={currentTool === TOOLS.PAN ? "primary" : "default"}
        onClick={() => onToolClick(TOOLS.PAN)}
      />
      <Button
        icon={<EditOutlined />}
        type={currentTool === TOOLS.PEN ? "primary" : "default"}
        onClick={() => onToolClick(TOOLS.PEN)}
      />
      <Button
        icon={<FontSizeOutlined />}
        type={currentTool === TOOLS.TEXT ? "primary" : "default"}
        onClick={() => onToolClick(TOOLS.TEXT)}
      />
      <Tooltip title="Callout">
        <Button
          icon={<MessageOutlined />}
          type={currentTool === TOOLS.CALLOUT ? "primary" : "default"}
          onClick={() => onToolClick(TOOLS.CALLOUT)}
        />
      </Tooltip>
      <Button
        icon={<LinkOutlined />}
        type={currentTool === TOOLS.CONNECTION ? "primary" : "default"}
        onClick={() => onToolClick(TOOLS.CONNECTION)}
      />
      {typeof onDetectSheetLinks === "function" && (
        <Tooltip title="Detect sheet links">
          <Button
            icon={<ScanOutlined />}
            loading={detectingSheetLinks}
            disabled={!canDetectSheetLinks}
            onClick={onDetectSheetLinks}
          />
        </Tooltip>
      )}
      <Button
        icon={<PlusOutlined />}
        type={currentTool === TOOLS.SHAPE ? "primary" : "default"}
        onClick={() => onToolClick(TOOLS.SHAPE)}
      />
      {currentTool === TOOLS.SHAPE && (
        <>
          <Button
            icon={<LineOutlined />}
            type={currentShape === SHAPES.LINE ? "primary" : "default"}
            onClick={() => onShapeClick(SHAPES.LINE)}
          />
          <Button
            icon={<BorderOutlined />}
            type={currentShape === SHAPES.RECTANGLE ? "primary" : "default"}
            onClick={() => onShapeClick(SHAPES.RECTANGLE)}
          />
          <Button
            icon={<Loading3QuartersOutlined />}
            type={currentShape === SHAPES.CIRCLE ? "primary" : "default"}
            onClick={() => onShapeClick(SHAPES.CIRCLE)}
          />
          <Button
            icon={<ArrowRightOutlined />}
            type={currentShape === SHAPES.ARROW ? "primary" : "default"}
            onClick={() => onShapeClick(SHAPES.ARROW)}
          />
          <Button
            icon={<CaretUpOutlined />}
            type={currentShape === SHAPES.POLYGON ? "primary" : "default"}
            onClick={() => onShapeClick(SHAPES.POLYGON)}
          />
          <Button
            icon={<CloudOutlined />}
            type={currentShape === SHAPES.CLOUD ? "primary" : "default"}
            onClick={() => onShapeClick(SHAPES.CLOUD)}
          />
        </>
      )}
      <div style={{ display: "flex", gap: 8 }}>
        {COLORS.map((c) => (
          <div
            key={c}
            onMouseDown={keepTextFocus}
            onClick={() => onColorChange(c)}
            style={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              background: c,
              border: color === c ? "3px solid #1890ff" : "2px solid #eee",
              cursor: "pointer",
              boxSizing: "border-box",
            }}
          />
        ))}
      </div>
      <Slider
        min={1}
        max={10}
        value={strokeWidth}
        onChange={onStrokeWidthChange}
        style={{ width: 100 }}
      />
      <Button
        icon={<DeleteOutlined />}
        onMouseDown={keepTextFocus}
        onClick={onDeleteAnnotation}
        disabled={!canDeleteSelectedAnnotation}
        title={
          selectedAnnotationIndex !== null && !canDeleteSelectedAnnotation
            ? "Consultants cannot delete saved annotations"
            : "Delete"
        }
      />
      {showTextControls && (
        <>
          <Button
            icon={<b>B</b>}
            type={textBold ? "primary" : "default"}
            onMouseDown={keepTextFocus}
            onClick={() => onTextStyleChange("bold", !textBold)}
          />
          <Button
            icon={<i>I</i>}
            type={textItalic ? "primary" : "default"}
            onMouseDown={keepTextFocus}
            onClick={() => onTextStyleChange("italic", !textItalic)}
          />
          <Button
            icon={<u>U</u>}
            type={textUnderline ? "primary" : "default"}
            onMouseDown={keepTextFocus}
            onClick={() => onTextStyleChange("underline", !textUnderline)}
          />
          <Button
            icon={<span style={{ textDecoration: "line-through" }}>S</span>}
            type={textStrike ? "primary" : "default"}
            onMouseDown={keepTextFocus}
            onClick={() => onTextStyleChange("strike", !textStrike)}
          />
          <div
            style={{ display: "flex", alignItems: "center", gap: 4 }}
            onMouseDown={keepTextFocus}
          >
            <Slider
              min={8}
              max={48}
              value={fontSize}
              onChange={onFontSizeChange}
              style={{ width: 80 }}
            />
            <span style={{ minWidth: 24, textAlign: "center" }}>
              {fontSize}
            </span>
          </div>
          <Button
            icon={
              <span
                style={{
                  background: "#ffe066",
                  padding: "0 4px",
                  borderRadius: 2,
                }}
              >
                H
              </span>
            }
            type={currentTool === TOOLS.HIGHLIGHT ? "primary" : "default"}
            onClick={() => onToolClick(TOOLS.HIGHLIGHT)}
          />
          <Button
            icon={
              <span style={{ textDecoration: "underline wavy red" }}>S</span>
            }
            type={currentTool === TOOLS.SQUIGGLY ? "primary" : "default"}
            onClick={() => onToolClick(TOOLS.SQUIGGLY)}
          />
        </>
      )}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          marginLeft: 8,
        }}
      >
        <Button
          onClick={() => onZoomChange(Math.max(0.1, zoom - 0.1))}
          style={{ fontWeight: 700, width: 32, padding: 0 }}
        >
          -
        </Button>
        <span style={{ minWidth: 40, textAlign: "center" }}>
          {Math.round(visualZoom * 100)}%
        </span>
        <Button
          onClick={() => onZoomChange(Math.min(5, zoom + 0.1))}
          style={{ fontWeight: 700, width: 32, padding: 0 }}
        >
          +
        </Button>
      </div>
      <div
        style={{
          marginLeft: 8,
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        <Button
          icon={<SearchOutlined />}
          type={showSearch ? "primary" : "default"}
          onClick={onSearchToggle}
        />
        {showSearch && (
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Input
              ref={searchInputRef}
              placeholder="Search in PDF..."
              value={searchQuery}
              onChange={onSearchChange}
              onPressEnter={() => {
                if (searchResults.length > 0) {
                  onSearchNavigate("next");
                }
              }}
              style={{ width: 200 }}
              suffix={
                isSearching ? (
                  <Spin size="small" />
                ) : searchResults.length > 0 ? (
                  <span style={{ fontSize: 12, color: "#666" }}>
                    {currentMatchIndex + 1}/{searchResults.length}
                  </span>
                ) : null
              }
            />
            {searchResults.length > 0 && (
              <>
                <Button
                  icon={<UpOutlined />}
                  onClick={() => onSearchNavigate("prev")}
                  disabled={searchResults.length === 0}
                  size="small"
                />
                <Button
                  icon={<DownOutlined />}
                  onClick={() => onSearchNavigate("next")}
                  disabled={searchResults.length === 0}
                  size="small"
                />
              </>
            )}
          </div>
        )}
      </div>
      <AskAIButton style={{ marginLeft: "auto" }} />
    </div>
  );
};

export default DrawingToolbar;

